/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import styled from '@emotion/styled'
import { CSSProperties, useState } from 'react'
import { Cautions } from '../../entites/FacilityCaution'
import {
  FacilityAvailableStatusCode,
  FacilityMedicalInstitutionType,
  FacilityPhoneSupportText,
} from '../../entites/FacilityInfo'
import { getDateText } from '../../utils/getDateText'
import { getPeriodStatus } from '../../utils/getPeriodStatus'
import { Avatar } from '../Avatar'
import { BookmarkButton } from '../BookmarkButton'
import { BookmarkButtonOutline } from '../BookmarkButtonOutline'
import { RatioImage } from '../RatioImage'
import { Access } from './Access'
import { Availability } from './Availability'
import { BackButton } from './BackButton'
import { BaseContainer } from './Base'
import { BasicInfo } from './BasicInfo'
import { ButtonLink } from './ButtonLink'
import { CardSection, Heading } from './CardSection'
import { CautionsView } from './CautionsView'
import { Checkpoints } from './Checkpoints'
import {
  ChildAvailable,
  ChildUnregistered,
  ChildrenAvailable,
  ChildrenBeforeRegistration,
} from './Children'
import { Clinic, Clinics } from './Clinics'
import { Description } from './Description'
import { FixRequestLink } from './FixRequestLink'
import { HeroContainer, ImageList } from './Hero'
import { PriceList, PriceOption } from './PriceList'
import { UnavailableDiseases } from './UnavailableDiseases'
import { VStack } from './VStack'

export type BasicInformationTemplateProps = {
  isUserLoggedIn: boolean
  isRegistrationEnable: boolean
  isBookmarked?: boolean
  isFacilityContracted: boolean
  isFacilityVisualizalized: boolean
  todayTelephoneSupportStartDate: Date | null
  todayTelephoneSupportEndDate: Date | null
  telephoneSupportText: FacilityPhoneSupportText | null
  reservationFunctionStartDate: Date | null
  reservationFunctionEndDate: Date | null
  id: number
  name: string
  attachedClinic?: Clinic
  imageUrls: string[]
  phoneNumber: string
  openingTimes: string[]
  childCapacityCount: number
  roomCount: number
  basicPrice: string
  distance: number | null
  mapUrl: string
  childCategory: string
  minAge: string
  maxAge: string
  availableAreas: string[]
  reservablePriorToRegistrationApproval: string
  lunchType: string
  medicalInstitutionType: FacilityMedicalInstitutionType
  cancelDeadline: string
  cancelPrice: string
  childrenAvailable: ChildAvailable[]
  childrenUnregistered: ChildUnregistered[]
  todayStatusCode: FacilityAvailableStatusCode
  tomorrowStatusCode: FacilityAvailableStatusCode
  todayStatusText: string
  tomorrowStatusText: string
  description: string
  collaborativeClinics: Clinic[]
  unavailableDiseases: string[]
  unavailableDiseasesText: string
  options: PriceOption[]
  optionDescription: string | null
  address: string
  siteUrl: string
  cautions: Cautions
  termUrl: string | null
  onUnbookmark?: (e: any) => void
  onBookmark?: (e: any) => void
  onBack?: (e: any) => void
  style?: CSSProperties
}

export const BasicInformationTemplate = ({
  isUserLoggedIn,
  isRegistrationEnable,
  isBookmarked,
  isFacilityContracted,
  todayTelephoneSupportStartDate,
  todayTelephoneSupportEndDate,
  telephoneSupportText,
  reservationFunctionStartDate,
  reservationFunctionEndDate,
  termUrl,
  isFacilityVisualizalized,
  id,
  name,
  attachedClinic,
  imageUrls,
  phoneNumber,
  openingTimes,
  childCapacityCount,
  roomCount,
  basicPrice,
  distance,
  mapUrl,
  childCategory,
  minAge,
  maxAge,
  availableAreas,
  reservablePriorToRegistrationApproval,
  lunchType,
  medicalInstitutionType,
  cancelDeadline,
  cancelPrice,
  childrenAvailable,
  childrenUnregistered,
  todayStatusCode,
  tomorrowStatusCode,
  todayStatusText,
  tomorrowStatusText,
  description,
  collaborativeClinics,
  unavailableDiseases,
  unavailableDiseasesText,
  options,
  optionDescription,
  address,
  siteUrl,
  cautions,
  onUnbookmark,
  onBookmark,
  onBack,
  style,
}: BasicInformationTemplateProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const bookmarkButtonStyle: CSSProperties = {
    position: 'absolute',
    right: '15px',
    bottom: '15px',
  }
  const bookmarkButton = isUserLoggedIn ? (
    isBookmarked ? (
      <BookmarkButton
        size={40}
        style={bookmarkButtonStyle}
        onClick={onUnbookmark}
      />
    ) : (
      <BookmarkButtonOutline
        size={40}
        style={bookmarkButtonStyle}
        onClick={onBookmark}
      />
    )
  ) : null
  const imageList = (
    <ImageList>
      {imageUrls.map((url, i) => {
        if (i > 2) return
        const border =
          i === selectedImageIndex
            ? '2px solid red'
            : '2px solid rgba(255, 255, 255, 0.5)'
        const style = css`
          border: ${border};
          cursor: pointer;
        `
        return (
          <Avatar
            key={i}
            src={url}
            size={42}
            css={style}
            onClick={() => setSelectedImageIndex(i)}
          />
        )
      })}
    </ImageList>
  )

  const todayTelephoneSupportStatus = getPeriodStatus(
    todayTelephoneSupportStartDate,
    todayTelephoneSupportEndDate,
  )
  const reservationFunctionStatus = getPeriodStatus(
    reservationFunctionStartDate,
    reservationFunctionEndDate,
  )

  const isTelephoneCallable = todayTelephoneSupportStatus === 'within'
  const isReservationEnable = reservationFunctionStatus === 'within'

  // 空き状況を表示するのは、
  // 1. 施設が単体契約なしかつ見える化グループ
  // 2. 施設が単体契約かつ予約申し込み機能稼働中かつ施設への登録機能稼働中
  // 3. 施設が単体契約かつ見える化グループかつ予約申込機能非稼働かつ施設への登録機能非稼働
  // 4. 施設が単体契約かつ見える化グループかつ予約申込機能稼働予定かつ施設への登録機能稼働中
  const showAvailability =
    (!isFacilityContracted && isFacilityVisualizalized) ||
    (isFacilityContracted && isReservationEnable && isRegistrationEnable) ||
    (isFacilityContracted &&
      isFacilityVisualizalized &&
      !isReservationEnable &&
      !isRegistrationEnable) ||
    (isFacilityContracted &&
      isFacilityVisualizalized &&
      reservationFunctionStatus === 'before' &&
      isRegistrationEnable)

  // 利用可能な子どもを表示するのは、
  // 1. 施設が単体契約 かつ 予約申込機能が稼働中 かつ ユーザーがログインしている
  const showAvailableChildren =
    isFacilityContracted && isReservationEnable && isUserLoggedIn

  // 登録していない子どもを表示するのは、
  // 1. 施設が単体契約 かつ 施設への登録機能が稼働中 かつ ユーザーがログインしている
  const showUnregisteredChildren =
    isFacilityContracted && isRegistrationEnable && isUserLoggedIn

  // 「ログインまたはアカウント作成」を表示するのは、
  // 1. 施設が単体契約 かつ （予約申込機能が稼働中 または 施設への登録機能が稼働中） かつ ユーザーが非ログイン
  const showSignInOrSignUp =
    isFacilityContracted &&
    (isReservationEnable || isRegistrationEnable) &&
    !isUserLoggedIn

  // 「電話する」を表示するのは、
  // 1. 施設が非単体契約
  // 2. 施設が単体契約 かつ 予約申込機能が非稼働
  const showCallButton =
    !isFacilityContracted || (isFacilityContracted && !isReservationEnable)

  const phoneSupportText = isFacilityVisualizalized
    ? telephoneSupportText
    : null

  return (
    <BaseContainer css={basicInformationTemplateStyle} style={style}>
      <HeroContainer>
        <RatioImage src={imageUrls[selectedImageIndex]} ratio={414 / 254} />
        {bookmarkButton}
        {imageList}
        <BackButton
          style={{
            position: 'absolute',
            top: '15px',
            left: '15px',
          }}
          onClick={onBack}
        />
      </HeroContainer>
      {reservationFunctionStatus === 'before' && (
        <div
          css={{
            padding: '30px 20px 0px',
          }}
        >
          <Notification>
            {reservationFunctionStartDate &&
              getDateText(reservationFunctionStartDate)}
            テオテ病児保育で予約受付開始
          </Notification>
        </div>
      )}
      <BasicInfo
        attachedClinic={attachedClinic ? attachedClinic.name : ''}
        name={name}
        phoneNumber={phoneNumber}
        openingTimes={openingTimes}
        childCapacityCount={childCapacityCount}
        roomCount={roomCount}
        basicPrice={basicPrice}
        distance={distance}
        mapUrl={mapUrl}
      />
      <VStack gap="15px" style={{ padding: '15px 15px 0px' }}>
        <Checkpoints
          childCategory={childCategory}
          minAge={minAge}
          maxAge={maxAge}
          areas={availableAreas}
          reservablePriorToRegistrationApproval={
            reservablePriorToRegistrationApproval
          }
          lunchType={lunchType}
          medicalInstitutionType={medicalInstitutionType}
          cancelDeadline={cancelDeadline}
          cancelPrice={cancelPrice}
          startGuidePageUrl={`/facility_details/${id}/usage_guide`}
          pricingPageUrl={`/facility_details/${id}/pricing`}
          termUrl={termUrl}
        />
        {showAvailability && (
          <Availability
            todayStatusCode={todayStatusCode}
            tomorrowStatusCode={tomorrowStatusCode}
            todayStatusText={todayStatusText}
            tomorrowStatusText={tomorrowStatusText}
          />
        )}
        {showAvailableChildren && (
          <ChildrenAvailable
            childList={childrenAvailable}
            reservationPageUrl={`/facilities/${id}`}
          />
        )}
        {showCallButton && (
          <CardSection>
            <Heading>この施設の予約</Heading>
            {phoneSupportText && (
              <p
                css={css`
                  font-size: 14px;
                  line-height: 100%;
                  text-align: center;
                  color: #3f3d35;
                  margin-bottom: 25.5px;

                  b {
                    font-size: 16px;
                    margin-left: 0.4em;
                  }
                `}
              >
                {phoneSupportText.label}
                <b>{phoneSupportText.emphasis}</b>
              </p>
            )}
            <ButtonLink
              href={'tel:' + phoneNumber}
              variant={
                isFacilityVisualizalized && !isTelephoneCallable
                  ? 'filled'
                  : 'outlined'
              }
              mainColor={
                isFacilityVisualizalized && !isTelephoneCallable
                  ? '#C9C9C6'
                  : undefined
              }
              disabled={isFacilityVisualizalized && !isTelephoneCallable}
              borderWidth="1px"
            >
              電話する
            </ButtonLink>
          </CardSection>
        )}
        {showUnregisteredChildren && (
          <ChildrenBeforeRegistration
            childList={childrenUnregistered}
            usagePageUrl={`/facility_details/${id}/usage_guide`}
            registrationPageUrl={`/user_child_facilities?facility_id=${id}`}
          />
        )}

        {showSignInOrSignUp && (
          <CardSection>
            <Heading
              style={{
                marginBottom: '25px',
              }}
            >
              {isReservationEnable ? 'この施設の予約' : 'この施設への登録'}
            </Heading>
            <ButtonLink
              href={`/users/sign_in?redirect_to=${location.pathname}`}
            >
              ログインまたはアカウント作成
            </ButtonLink>
          </CardSection>
        )}
      </VStack>
      <h2>🏠 施設の紹介</h2>
      <Description description={description} />
      <VStack gap="15px" style={{ padding: '30px 15px 0px' }}>
        <Clinics
          medicalInstitutionType={medicalInstitutionType}
          attached={attachedClinic}
          collaboratives={collaborativeClinics}
        />
        <UnavailableDiseases
          diseases={unavailableDiseases}
          text={unavailableDiseasesText}
        />
        <PriceList
          heading="ご利用可能なオプション"
          options={options.filter(
            (option) => option && option.title && option.title.length > 0,
          )}
          note={optionDescription}
        />
        <Access
          address={address}
          mapUrl={mapUrl}
          phoneNumber={phoneNumber}
          siteUrl={siteUrl}
        />
        <CautionsView cautions={cautions} />
      </VStack>
      <div
        style={{
          padding: '30px 20px 30px',
        }}
      >
        <FixRequestLink>情報の修正を提案</FixRequestLink>
      </div>
    </BaseContainer>
  )
}

const basicInformationTemplateStyle = css`
  h2 {
    font-size: 18px;
    line-height: 100%;
    text-align: center;
    letter-spacing: 0.05em;

    border-top: 1px solid #edeceb;
    padding-top: 30px;
    margin-top: 30px;
  }
`

const Notification = styled.div(css`
  height: 30px;
  font-size: 12px;
  line-height: 160%;
  color: #ae000e;
  border: 1px solid currentColor;
  box-sizing: border-box;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`)
